import React from "react";
import styled from "styled-components";

import type { Amount } from "../../../../../shared/core/amount/amount";
import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { AccountingEntry } from "../../../../../shared/domains/accounting-transaction/accounting-entry";
import type { BillIssuer } from "../../../../../shared/domains/bills/bill";
import { PrimaryButton } from "../../../common/buttons/primary-button";
import { MarkdownText } from "../../../common/forms/markdown-text";
import { SideEntryLabel } from "../../../common/transaction/side-entry-label";
import { theme } from "../../../styles/theme";

interface BillConfirmationViewProps {
  issuer?: BillIssuer;
  transactionDetails?: AccountingEntry[];
  amount?: Amount;
  onSubmit?: () => void;
}

export const BillConfirmationView = (props: BillConfirmationViewProps) => {
  const { issuer, transactionDetails, amount, onSubmit } = props;
  const { formatMessage, formatAmount } = useIntl();

  return (
    <Wrapper>
      <DetailsWrapper>
        <Details>
          <SummaryText>
            {formatMessage("billFormScreen.confirmationMessage", {
              amount: formatAmount(amount, true),
              issuer: issuer?.name,
            })}
          </SummaryText>
          {transactionDetails &&
            transactionDetails
              .filter((entry) => entry.type !== "TRANSFER")
              .map((entry, index) => (
                <Name key={`${entry.type}_${index}`}>
                  <SideEntryLabel entry={entry} translationKeyPrefix="billConfirmationScreen" />
                </Name>
              ))}
        </Details>
      </DetailsWrapper>
      <ButtonContainer>
        <PrimaryButton size="M" style={{ marginBottom: 15 }} onClick={onSubmit}>
          {formatMessage("billFormScreen.confirmButton")}
        </PrimaryButton>
      </ButtonContainer>
    </Wrapper>
  );
};

const SummaryText = styled(MarkdownText)`
  font-size: 15px;
  text-align: center;
  color: #aaa;
  width: 80%;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Details = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
`;

const DetailsWrapper = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
  flex: 1;
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Name = styled.div`
  ${theme.mediumText};
  font-size: 0.875rem;
  color: #aaaaaa;
  white-space: pre-line;
`;

const Wrapper = styled(FlexColumn)`
  flex: 1;
  overflow: hidden;
  align-items: center;
  justify-content: space-between;
`;
